import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Alert,
    Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { workshopDomain } from '../util/domain';
import { getEventTypeLabel, getEventTypeColor } from '../util/eventTypes';

const EventList = () => {
    const [events, setEvents] = useState([]);
    const [error, setError] = useState('');
    const [imageRefreshTimestamp, setImageRefreshTimestamp] = useState(Date.now());
    const navigate = useNavigate();

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${workshopDomain}/api/events`);
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            setError('Failed to fetch events');
        }
    };

    const handleRegister = (eventHashId) => {
        navigate(`/register/${eventHashId}`);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN':
                return 'success';
            case 'UPCOMING':
                return 'warning';
            case 'CLOSED':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Show only active events
    const activeEvents = events.filter(event => event.is_active);

    const getRegistrationUrl = (hashId) => {
        const origin = window.location.origin;
        return `${origin}/register/${hashId}`;
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {activeEvents.length === 0 && (
                    <Box sx={{
                        minHeight: '400px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                    }}>
                        {/* Blank banner when no events available */}
                    </Box>
                )}

                <Grid container spacing={3}>
                    {activeEvents.map((event) => (
                        <Grid item xs={12} key={event.hash_id}>
                            <Card>
                                <CardContent>
                                    {/* Display banner if it exists */}
                                    <Box
                                        component="img"
                                        src={`${workshopDomain}/workshop/${event.hash_id}.jpeg?v=${imageRefreshTimestamp}`}
                                        key={`${event.hash_id}-${events.length}`}
                                        alt={event.title}
                                        sx={{
                                            width: '100%',
                                            objectFit: 'contain',
                                            borderRadius: 1,
                                            mb: 2,
                                            display: 'block',
                                            minHeight: '400px',
                                            backgroundColor: '#f5f5f5'
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h5" component="h2">
                                                {event.title}
                                            </Typography>
                                            <Chip
                                                label={event.event_type}
                                                color={getEventTypeColor(event.event_type)}
                                                size="small"
                                                sx={{ mr: 1, mt: 1 }}
                                            />
                                            <Chip
                                                label={event.is_online ? 'Online' : 'Offline'}
                                                variant="outlined"
                                                size="small"
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>
                                        <Chip
                                            label={event.registration_status}
                                            color={getStatusColor(event.registration_status)}
                                            size="small"
                                        />
                                    </Box>
                                    {/* Only show required fields */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Venue:</strong> {event.is_online ? 'Online Event' : event.venue}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>{getEventTypeLabel(event.event_type)} Dates:</strong><br />
                                            {formatDate(event.event_start_date)} - {formatDate(event.event_end_date)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            <strong>Event Registration URL:</strong><br />
                                            <a href={getRegistrationUrl(event.hash_id)}>
                                                {getRegistrationUrl(event.hash_id)}
                                            </a>
                                        </Typography>
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => handleRegister(event.hash_id)}
                                        disabled={event.registration_status !== 'OPEN' || event.available_slots <= 0}
                                    >
                                        {event.registration_status === 'OPEN' ? 'Register Now' : 'Registration Closed'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Container>
    );
};

export default EventList; 